import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getAnalyticsData,
  getAnalyticsSummary,
  getAnalyticsOptions,
  GROUPING_OPTIONS,
  DATE_PERIOD_OPTIONS,
} from '../api/analyticsApi'

export function useAnalyticsListScreen() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [groupBy, setGroupBy] = useState(searchParams.get('groupBy') || 'institute')
  const [chartMode, setChartMode] = useState('bar')
  const [tab, setTab] = useState(searchParams.get('tab') || 'active')

  const [company, setCompany] = useState('All')
  const [branch, setBranch] = useState('All')
  const [course, setCourse] = useState('All')
  const [batch, setBatch] = useState('All')
  const [gender, setGender] = useState('All')
  const [counsellor, setCounsellor] = useState('All')
  const [datePeriod, setDatePeriod] = useState('period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [dimensionLabel, setDimensionLabel] = useState('Company / Institute')
  const [summary, setSummary] = useState({
    totalStudents: 0,
    activeSegments: 0,
    topSegmentName: '—',
    topSegmentCount: 0,
    topSegmentPercentage: 0.0,
    maleCount: 0,
    femaleCount: 0,
    otherCount: 0,
  })

  const [options, setOptions] = useState({
    companies: ['All'],
    branches: ['All'],
    courses: [],
    batches: [],
    counsellors: [],
    genders: ['All', 'Male', 'Female', 'Others'],
    dimensions: GROUPING_OPTIONS,
    datePeriodOptions: DATE_PERIOD_OPTIONS,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // Load filter options on mount
  useEffect(() => {
    let isMounted = true
    getAnalyticsOptions()
      .then((res) => {
        if (isMounted && res) {
          setOptions({
            companies: ['All', ...(res.companies || []).filter((c) => c !== 'All')],
            branches: ['All', ...(res.branches || []).filter((b) => b !== 'All')],
            courses: ['All', ...(res.courses || []).filter((c) => c !== 'All')],
            batches: ['All', ...(res.batches || []).filter((b) => b !== 'All')],
            counsellors: ['All', ...(res.counsellors || []).filter((c) => c !== 'All')],
            genders: ['All', 'Male', 'Female', 'Others'],
            dimensions: res.dimensions && res.dimensions.length > 0 ? res.dimensions : GROUPING_OPTIONS,
            datePeriodOptions: DATE_PERIOD_OPTIONS,
          })
        }
      })
      .catch(() => {})
    return () => {
      isMounted = false
    }
  }, [])

  const toISODate = (date) => {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleDatePeriodChange = (preset) => {
    setDatePeriod(preset)
    if (preset === 'period') {
      setFromDate('')
      setToDate('')
      return
    }

    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const sunday = new Date(startOfDay)
    sunday.setDate(startOfDay.getDate() - ((startOfDay.getDay() + 6) % 7))

    const dayOffset = (days) => new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + days)

    const ranges = {
      today: [startOfDay, startOfDay],
      yesterday: [new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1), new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)],
      current_week: [dayOffset(0), dayOffset(6)],
      current_month: [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)],
      current_year: [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)],
      last_week: [dayOffset(-7), dayOffset(-1)],
      last_month: [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)],
      last_year: [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)],
    }

    const range = ranges[preset]
    if (range) {
      setFromDate(toISODate(range[0]))
      setToDate(toISODate(range[1]))
    }
  }

  const queryParams = useMemo(
    () => ({
      groupBy,
      company,
      branch,
      course,
      batch,
      gender,
      counsellor,
      period: datePeriod,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      tab,
    }),
    [groupBy, company, branch, course, batch, gender, counsellor, datePeriod, fromDate, toDate, tab]
  )

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const [resData, resSummary] = await Promise.all([
        getAnalyticsData(queryParams),
        getAnalyticsSummary(queryParams),
      ])

      const items = resData.items || []
      setData(items)
      setTotalRecords(resData.totalCount ?? items.reduce((s, i) => s + (i.count || 0), 0))
      setDimensionLabel(resData.dimensionLabel || 'Dimension')
      setSummary(resSummary)
    } catch (err) {
      setError('Failed to load analytics data. Please try again.')
      setData([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }, [queryParams])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  const handleTabChange = (nextTab) => {
    setTab(nextTab)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', nextTab)
      return next
    })
  }

  const handleDimensionChange = (nextDimension) => {
    setGroupBy(nextDimension)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('groupBy', nextDimension)
      return next
    })
  }

  const resetFilters = () => {
    setCompany('All')
    setBranch('All')
    setCourse('All')
    setBatch('All')
    setGender('All')
    setCounsellor('All')
    setDatePeriod('period')
    setFromDate('')
    setToDate('')
  }

  const topSegment = useMemo(() => {
    if (!data.length) return { name: '—', count: 0, percentage: 0 }
    return data[0]
  }, [data])

  return {
    state: {
      groupBy,
      chartMode,
      tab,
      company,
      branch,
      course,
      batch,
      gender,
      counsellor,
      datePeriod,
      fromDate,
      toDate,
      data,
      totalRecords,
      dimensionLabel,
      summary,
      topSegment,
      options,
      isLoading,
      error,
    },
    actions: {
      setGroupBy: handleDimensionChange,
      setChartMode,
      setTab: handleTabChange,
      setCompany,
      setBranch,
      setCourse,
      setBatch,
      setGender,
      setCounsellor,
      setDatePeriod,
      handleDatePeriodChange,
      setFromDate,
      setToDate,
      search: loadAnalytics,
      resetFilters,
      refresh: loadAnalytics,
    },
  }
}

export default useAnalyticsListScreen
